import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { Box } from "@mui/material";

interface PageTransitionProps {
  children: React.ReactNode;
}

export default function PageTransition({ children }: PageTransitionProps) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [transitionStage, setTransitionStage] = useState<"fadeIn" | "fadeOut" | "idle">(
    "idle",
  );
  const [isBackNavigation, setIsBackNavigation] = useState(false);
  const prevLocationRef = useRef(location);

  useEffect(() => {
    if (location !== prevLocationRef.current) {
      // Detect if this is a back navigation
      setIsBackNavigation(navigationType === "POP");
      setTransitionStage("fadeOut");
      prevLocationRef.current = location;
    }
  }, [location, navigationType]);

  const onAnimationEnd = () => {
    if (transitionStage === "fadeOut") {
      setTransitionStage("fadeIn");
    } else if (transitionStage === "fadeIn") {
      setTransitionStage("idle");
    }
  };

  // Choose animation based on navigation direction
  const slideInAnimation = isBackNavigation
    ? "slideInLeft 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards"
    : "slideInRight 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards";

  const slideOutAnimation = isBackNavigation
    ? "slideOutRight 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards"
    : "slideOutLeft 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards";

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: "100vh",
        animation:
          transitionStage === "fadeIn"
            ? slideInAnimation
            : transitionStage === "fadeOut"
            ? slideOutAnimation
            : "none",
        ...(transitionStage !== "idle" && {
          willChange: "transform, opacity",
          backfaceVisibility: "hidden",
          WebkitBackfaceVisibility: "hidden",
          transform: "translateZ(0)",
          WebkitTransform: "translateZ(0)",
        }),
        "@keyframes slideInRight": {
          "0%": {
            transform: "translate3d(100%, 0, 0)",
            opacity: 0,
          },
          "100%": {
            transform: "translate3d(0, 0, 0)",
            opacity: 1,
          },
        },
        "@keyframes slideInLeft": {
          "0%": {
            transform: "translate3d(-30%, 0, 0)",
            opacity: 0,
          },
          "100%": {
            transform: "translate3d(0, 0, 0)",
            opacity: 1,
          },
        },
        "@keyframes slideOutLeft": {
          "0%": {
            transform: "translate3d(0, 0, 0)",
            opacity: 1,
          },
          "100%": {
            transform: "translate3d(-30%, 0, 0)",
            opacity: 0,
          },
        },
        "@keyframes slideOutRight": {
          "0%": {
            transform: "translate3d(0, 0, 0)",
            opacity: 1,
          },
          "100%": {
            transform: "translate3d(100%, 0, 0)",
            opacity: 0,
          },
        },
      }}
      onAnimationEnd={onAnimationEnd}
    >
      {children}
    </Box>
  );
}
